// Протокол в режиме ведения (011: data-model.md, «Рабочее состояние»; contracts/api.md,
// TransferPreview и ProtocolState). Режим документа — documents.documents.mode (ADR-0031).
import { z } from '../kernel/zod.ts';
import { DateSchema, IdSchema, PersonRefSchema, TimestampSchema } from '../kernel/common.ts';
import { ErrorCodeSchema } from '../errors/catalog.ts';
import { ExtraFieldKeySchema, ItemRowSchema } from './item.ts';
import { ProtocolAccessSchema } from './access.ts';
import { ProtocolViewSchema } from './view.ts';

/** file — контроль по загрузкам (переходное состояние), maintained — протокол ведётся в системе. */
export const documentModes = ['file', 'maintained'] as const;
export type DocumentMode = (typeof documentModes)[number];
export const DocumentModeSchema = z.enum(documentModes);

export const documentModeLabels = {
  file: 'Файл',
  maintained: 'Ведётся в системе',
} as const satisfies Record<DocumentMode, string>;

/**
 * Колонка таблицы протокола: встроенное поле или дополнительное (FR-022). 014: колонки полей
 * пункта (FR-027); «Ответственный» — `assignees`, `responsibleRaw` — до сходимости среза.
 */
export const builtinColumnKeys = [
  'numberRaw',
  'title',
  'responsibleRaw',
  'dueRaw',
  'noteHtml',
  'assignees',
  'startRaw',
  'contractor',
  'actualEndRaw',
  'workState',
  'workNote',
  'progress',
] as const;
export type BuiltinColumnKey = (typeof builtinColumnKeys)[number];

export const ProtocolColumnSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('builtin'),
    key: z.enum(builtinColumnKeys),
    label: z.string(),
    /**
     * Колонка есть в файле — схеме шаблона (решение владельца 25.10.2026): по умолчанию видна
     * всегда. Нет признака — поле единой формы, которого в файле нет: включается в «Доп. полях».
     */
    file: z.boolean().optional(),
    /**
     * Колонка подрядчика файла, из которой взяты и «Подрядчик», и «Фактическое окончание» (021:
     * research, Р3): подпись колонки в файле. Печать и XLSX выводят оба поля одной колонкой.
     */
    fileLabel: z.string().optional(),
  }),
  z.object({ kind: z.literal('extra'), key: ExtraFieldKeySchema, label: z.string() }),
]);
export type ProtocolColumn = z.infer<typeof ProtocolColumnSchema>;

/** Предпросмотр перевода: числа для окна подтверждения (FR-004). */
export const TransferPreviewSchema = z.object({
  documentId: IdSchema,
  versionId: IdSchema,
  sheet: z.string(),
  sheetDate: DateSchema,
  nodes: z.number().int().min(0),
  hidden: z.number().int().min(0),
  uncertain: z.number().int().min(0),
  partial: z.number().int().min(0),
  /** Примечаний с оформлением фраз (ячейки rich text колонки примечания). */
  richCells: z.number().int().min(0),
  /** Ячеек листа с оформлением ячейки: жирный, курсив, зачёркнутый, цвет, заливка (013: FR-040). */
  formattedCells: z.number().int().min(0),
  /** Строки тела листа, которые не стали пунктами: продолжения объединений и пустые (B110, B368). */
  skipped: z.number().int().min(0),
  extraFields: z.array(z.object({ label: z.string(), column: z.string() })),
  /** Почему перевод сейчас невозможен (MIN-002, MIN-003); null — можно переводить. */
  blocked: z.object({ code: ErrorCodeSchema, reason: z.string() }).nullable(),
  /**
   * 031 (FR-015; ASG-RES-14): ответственные листа перевода — всего, связано с человеком, вопросов,
   * неясно, без годной почты. Нет данных справочника — null.
   */
  people: z
    .object({
      total: z.number().int().min(0),
      linked: z.number().int().min(0),
      questions: z.number().int().min(0),
      unclear: z.number().int().min(0),
      noEmail: z.number().int().min(0),
    })
    .nullable()
    .optional(),
});
export type TransferPreview = z.infer<typeof TransferPreviewSchema>;

/** Протокол в режиме ведения целиком: колонки по схеме шаблона, пункты в порядке дерева. */
export const ProtocolStateSchema = z.object({
  lastSnapshotRevision: z.number().int().min(0).optional(),
  documentId: IdSchema,
  /** П-N. */
  number: z.number().int().min(1),
  title: z.string(),
  revision: z.number().int().min(1),
  structureRevision: z.number().int().min(1),
  /** Ревизия перевода текущего поколения: правки после перевода — revision − transferRevision. */
  transferRevision: z.number().int().min(1),
  columns: z.array(ProtocolColumnSchema),
  /** Пункты текущего поколения: действующие и архивные (archivedAt), в порядке дерева. */
  items: z.array(ItemRowSchema),
  transferredAt: TimestampSchema,
  transferredBy: PersonRefSchema,
  /** Лист версии-источника и его дата — чипы шапки (012: ux CHK003); даты может не быть. */
  sourceSheet: z.string(),
  sheetDate: DateSchema.nullable(),
  /** Личный вид пользователя (012: research, Р11). */
  view: ProtocolViewSchema,
  /** От стольких строк ветви архив спрашивает подтверждение (настройка, Р17). */
  archiveConfirmThreshold: z.number().int().min(2),
  /** «Сегодня» по поясу установки — просрочка гибкого срока (014: OPEN-07). */
  today: DateSchema,
  /** file — переведён из файла; system — создан в системе без файла (014: FR-032). */
  origin: z.enum(['file', 'system']),
  /** Дата рабочей недели (016: FR-001); сдвигается фиксацией. */
  weekDate: DateSchema,
  /** Число снимков недели — на ярлыке вкладки «Недели». */
  snapshotCount: z.number().int().min(0),
  /** Права пользователя в протоколе (020: research, Р6). */
  access: ProtocolAccessSchema,
});
export type ProtocolState = z.infer<typeof ProtocolStateSchema>;
